import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarManagementModal } from '@/components/calendar/CalendarManagementModal';
import { CampaignModal } from '@/components/calendar/CampaignModal';
import { PostCreator } from '@/components/PostCreator';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ChannelSettings } from '@/components/settings/ChannelSettings';
import { useAuth } from '@/hooks/useAuth';
import { useInviteToTeam } from '@/hooks/useInvitations';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useCalendars } from '@/hooks/useCalendar';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import type { CreateNoteData, UpdateNoteData } from '@/types/calendar-notes';

/**
 * OnboardingActions Component
 *
 * Centralized handler for all onboarding checklist action events.
 * Listens for custom events dispatched by FloatingChecklist and opens appropriate modals.
 *
 * Events handled:
 * - open-channel-modal → ChannelSettings modal
 * - open-calendar-modal → CalendarManagementModal
 * - open-post-creator → PostCreator modal (NOT PostComposer drawer - deprecated)
 * - open-campaign-modal → CampaignModal
 * - open-invite-modal → Team Invite modal
 *
 * This component should be added to App.tsx inside OnboardingProvider.
 */
export function OnboardingActions() {
  const { t } = useTranslation(['settings', 'calendar', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { teamId } = useEffectiveTeamId();
  const rolesLock = useFeatureLock('team_roles');

  // Fetch calendars to get the first available calendar for campaign creation
  const { data: calendars } = useCalendars({ isActive: true });
  const firstCalendarId = calendars && calendars.length > 0 ? calendars[0].id : null;

  // Modal states
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [postCreatorOpen, setPostCreatorOpen] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER' | 'REVIEWER'>('MEMBER');
  const [inviteMessage, setInviteMessage] = useState('');

  // Mutations
  const inviteMutation = useInviteToTeam();

  // Check if user is in God Mode or agency client workspace
  const isGodMode = !!user?.godModeSession;
  const isAgencyClientWorkspace = isGodMode; // Simplified check

  // Set up event listeners
  useEffect(() => {
    const handleOpenChannelModal = () => {
      setChannelModalOpen(true);
    };

    const handleOpenCalendarModal = () => {
      setCalendarModalOpen(true);
    };

    const handleOpenPostCreator = () => {
      setPostCreatorOpen(true);
    };

    const handleOpenCampaignModal = () => {
      // Navigate to calendar first if not already there
      if (window.location.pathname !== '/calendar') {
        navigate('/calendar');
        // Delay modal opening to allow calendar page to load
        setTimeout(() => {
          setCampaignModalOpen(true);
        }, 300);
      } else {
        setCampaignModalOpen(true);
      }
    };

    const handleOpenInviteModal = () => {
      // Set default role based on workspace type
      setInviteRole(isAgencyClientWorkspace ? 'REVIEWER' : 'MEMBER');
      setInviteModalOpen(true);
    };

    // Register event listeners
    window.addEventListener('open-channel-modal', handleOpenChannelModal);
    window.addEventListener('open-calendar-modal', handleOpenCalendarModal);
    window.addEventListener('open-post-creator', handleOpenPostCreator);
    window.addEventListener('open-campaign-modal', handleOpenCampaignModal);
    window.addEventListener('open-invite-modal', handleOpenInviteModal);

    // Cleanup
    return () => {
      window.removeEventListener('open-channel-modal', handleOpenChannelModal);
      window.removeEventListener('open-calendar-modal', handleOpenCalendarModal);
      window.removeEventListener('open-post-creator', handleOpenPostCreator);
      window.removeEventListener('open-campaign-modal', handleOpenCampaignModal);
      window.removeEventListener('open-invite-modal', handleOpenInviteModal);
    };
  }, [navigate, isAgencyClientWorkspace]);

  // Handle invite submission
  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      toast.error(t('settings_team_error_email_required'));
      return;
    }

    if (!isValidEmail(inviteEmail)) {
      toast.error(t('settings_team_error_invalid_email', { defaultValue: 'Please enter a valid email address' }));
      return;
    }

    // Only proceed if we have a valid teamId
    if (!teamId) {
      toast.error(t('settings_team_error_no_team', { defaultValue: 'No team found' }));
      return;
    }

    inviteMutation.mutate(
      {
        teamId,
        data: {
          email: inviteEmail.trim(),
          role: rolesLock.locked && !isAgencyClientWorkspace ? 'MEMBER' : inviteRole,
          message: inviteMessage.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setInviteModalOpen(false);
          setInviteEmail('');
          setInviteRole(isAgencyClientWorkspace ? 'REVIEWER' : 'MEMBER');
          setInviteMessage('');
          toast.success(t('settings_team_success_invite'));
          // Refresh invitations list
          queryClient.invalidateQueries({ queryKey: ['team-invitations', teamId] });
        },
        onError: (err) => {
          toast.error(formatApiError(err).message);
        },
      }
    );
  };

  // Handle campaign submission (placeholder - actual logic should be in Calendar page)
  const handleCampaignSubmit = (_data: CreateNoteData | { id: number; data: UpdateNoteData }) => {
    // This is a placeholder - in production, this should either:
    // 1. Call the campaign API directly
    // 2. Pass through to the Calendar page's campaign handler
    setCampaignModalOpen(false);
    toast.success(t('calendar:calendar_campaign_created'));
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Role options for invite modal
  const roleOptions = isAgencyClientWorkspace
    ? [
        {
          value: 'REVIEWER' as const,
          label: t('settings_modal_invite_role_reviewer'),
          icon: ICONS.CHECK,
          description: t('settings_modal_invite_role_reviewer_desc'),
        },
      ]
    : [
        {
          value: 'MEMBER' as const,
          label: t('settings_modal_invite_role_member'),
          icon: ICONS.USER,
          description: t('settings_modal_invite_role_member_desc'),
        },
        // team_roles locked: the backend downgrades invites to the default role, so only that role is offered
        ...(rolesLock.locked
          ? []
          : [
              {
                value: 'ADMIN' as const,
                label: t('settings_modal_invite_role_admin'),
                icon: ICONS.SHIELD,
                description: t('settings_modal_invite_role_admin_desc'),
              },
            ]),
      ];

  return (
    <>
      {/* Channel Settings Modal */}
      <Modal
        open={channelModalOpen}
        onOpenChange={setChannelModalOpen}
        title={t('settings_channel_header_title')}
        size="xl"
        footer={null}
      >
        <ChannelSettings />
      </Modal>

      {/* Calendar Management Modal */}
      <CalendarManagementModal
        isOpen={calendarModalOpen}
        onClose={() => setCalendarModalOpen(false)}
      />

      {/* Post Creator Modal */}
      <PostCreator
        isOpen={postCreatorOpen}
        onClose={() => setPostCreatorOpen(false)}
        onSaveSuccess={() => {
          toast.success(t('post_created_success', { defaultValue: 'Post created successfully!' }));
        }}
      />

      {/* Campaign Modal - only render if we have a calendar */}
      {firstCalendarId && (
        <CampaignModal
          isOpen={campaignModalOpen}
          onClose={() => setCampaignModalOpen(false)}
          calendarId={firstCalendarId}
          onSubmit={handleCampaignSubmit}
          isLoading={false}
          showRepetition={false}
          modalTitle={t('create_campaign', { defaultValue: 'Create Campaign' })}
        />
      )}
      {/* Show message if no calendar exists */}
      {campaignModalOpen && !firstCalendarId && (
        <Modal
          open={campaignModalOpen}
          onOpenChange={setCampaignModalOpen}
          title={t('no_calendar_found', { defaultValue: 'No Calendar Found' })}
          size="sm"
          footer={null}
        >
          <div className="py-8 text-center">
            <Icon name={ICONS.CALENDAR} size="lg" color="text-gray-400" ariaHidden />
            <p className="mt-4 text-gray-600">
              {t('create_calendar_first', { defaultValue: 'Please create a calendar first before setting up a campaign.' })}
            </p>
            <Button
              variant="primary"
              size="md"
              className="mt-6"
              onClick={() => {
                setCampaignModalOpen(false);
                setCalendarModalOpen(true);
              }}
            >
              {t('create_calendar', { defaultValue: 'Create Calendar' })}
            </Button>
          </div>
        </Modal>
      )}

      {/* Team Invite Modal */}
      <Modal
        open={inviteModalOpen}
        onOpenChange={(open) => {
          setInviteModalOpen(open);
          if (!open) {
            setInviteEmail('');
            setInviteRole(isAgencyClientWorkspace ? 'REVIEWER' : 'MEMBER');
            setInviteMessage('');
          }
        }}
        title=""
        size="md"
        footer={null}
      >
        <div className="space-y-6">
          {/* Hero Header */}
          <div className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary-400 flex items-center justify-center mb-4">
              <Icon name={ICONS.USER} size="lg" color="text-white" ariaHidden />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('settings_modal_invite_title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('settings_modal_invite_subtitle')}
            </p>
          </div>

          {/* Email Input with validation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings_modal_invite_email_label')}
            </label>
            <div className="relative">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('settings_modal_invite_email_placeholder')}
                className="pr-10"
                autoFocus
              />
              {/* Validation icon */}
              {inviteEmail && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isValidEmail(inviteEmail) ? (
                    <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-500" ariaHidden />
                  ) : (
                    <Icon name={ICONS.CROSS_CIRCLE} size="sm" color="text-red-400" ariaHidden />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Role Selection Cards */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('settings_modal_invite_role_label')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {roleOptions.map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => setInviteRole(role.value)}
                  className={cn(
                    'p-4 rounded-xl border-2 text-left transition-all',
                    inviteRole === role.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <Icon
                      name={role.icon}
                      size="sm"
                      color={inviteRole === role.value ? 'text-primary-600' : 'text-gray-400'}
                      ariaHidden
                    />
                    <span
                      className={cn(
                        'font-medium',
                        inviteRole === role.value
                          ? 'text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {role.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
                    {role.description}
                  </p>
                </button>
              ))}
            </div>
            {rolesLock.locked && !isAgencyClientWorkspace && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500" data-testid="invite-roles-locked-hint">
                <Icon name={ICONS.LOCK} size="xs" ariaHidden />
                {t('common:gating_team_roles_invite_hint')}
              </p>
            )}
          </div>

          {/* Optional Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings_modal_invite_message_label', { defaultValue: 'Personal message (optional)' })}
            </label>
            <Textarea
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              placeholder={t('settings_modal_invite_message_placeholder', { defaultValue: "Hey! I'd love for you to join our team..." })}
              rows={3}
              maxLength={500}
              showCharCount
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setInviteModalOpen(false);
                setInviteEmail('');
                setInviteRole(isAgencyClientWorkspace ? 'REVIEWER' : 'MEMBER');
                setInviteMessage('');
              }}
              disabled={inviteMutation.isPending}
            >
              {t('settings_modal_cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleInvite}
              disabled={inviteMutation.isPending || !inviteEmail.trim() || !isValidEmail(inviteEmail)}
              loading={inviteMutation.isPending}
            >
              {inviteMutation.isPending ? (
                t('settings_modal_invite_sending')
              ) : (
                <>
                  {t('settings_modal_invite_send')}
                  <Icon name={ICONS.ARROW_RIGHT} size="sm" className="ml-2" ariaHidden />
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
